/**
 * S13 — Éditeur de tarifs spécifiques par client
 * Composant pour configurer les prix négociés par client pour un produit
 * Design Dashboard : bg-white, text-gray-900, teal-600, teal-600 (accent client pricing)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, UserCheck, AlertCircle, Search } from 'lucide-react';
import { productsAPI } from '@/utils/productsAPI';
import { clientsAPI } from '@/utils/clientsAPI';
import type { ProductClientPriceEntry } from '@/types/models';

interface ClientPricingEditorProps {
  productId: string;
  baseUnitPrice: number;
}

const clientPricingKeys = {
  all: ['clientPricing'] as const,
  byProduct: (productId: string) => [...clientPricingKeys.all, 'product', productId] as const,
};

export const ClientPricingEditor: React.FC<ClientPricingEditorProps> = ({
  productId,
  baseUnitPrice,
}) => {
  const { t } = useTranslation('products');
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [newClientId, setNewClientId] = useState('');
  const [newPrice, setNewPrice] = useState<number | ''>('');
  const [newNotes, setNewNotes] = useState('');
  const [newValidFrom, setNewValidFrom] = useState('');
  const [newValidUntil, setNewValidUntil] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Charger les tarifs clients existants
  const { data: clientPrices = [], isLoading } = useQuery({
    queryKey: clientPricingKeys.byProduct(productId),
    queryFn: () => productsAPI.getProductClientPrices(productId),
    staleTime: 30_000,
  });

  // Charger tous les clients une seule fois (même pattern que EditableClientSection)
  const { data: allClients = [], isLoading: isLoadingClients } = useQuery({
    queryKey: ['clients', 'all-for-pricing'],
    queryFn: async () => {
      const result = await clientsAPI.getAll({ limit: 100 });
      return result.clients;
    },
    enabled: showForm,
    staleTime: 60_000,
  });

  // Mutation: définir un tarif
  const setMutation = useMutation({
    mutationFn: (data: { clientId: string; customPrice: number; notes?: string; validFrom?: string; validUntil?: string }) =>
      productsAPI.setProductClientPrice(productId, data.clientId, {
        customPrice: data.customPrice,
        notes: data.notes,
        validFrom: data.validFrom,
        validUntil: data.validUntil,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientPricingKeys.byProduct(productId) });
      resetForm();
    },
  });

  // Mutation: supprimer un tarif
  const [removeError, setRemoveError] = useState<string | null>(null);
  const removeMutation = useMutation({
    mutationFn: (clientId: string) => productsAPI.removeProductClientPrice(productId, clientId),
    onSuccess: () => {
      setRemoveError(null);
      queryClient.invalidateQueries({ queryKey: clientPricingKeys.byProduct(productId) });
    },
    onError: (error: Error) => {
      setRemoveError(error.message || t('products.clientPricing.removeError', { defaultValue: 'Erreur lors de la suppression' }));
    },
  });

  const resetForm = () => {
    setShowForm(false);
    setNewClientId('');
    setNewPrice('');
    setNewNotes('');
    setNewValidFrom('');
    setNewValidUntil('');
    setClientSearch('');
    setShowDropdown(false);
  };

  const handleSubmit = () => {
    if (!newClientId || newPrice === '' || newPrice < 0) return;
    setMutation.mutate({
      clientId: newClientId,
      customPrice: newPrice,
      notes: newNotes || undefined,
      validFrom: newValidFrom ? new Date(newValidFrom).toISOString() : undefined,
      validUntil: newValidUntil ? new Date(newValidUntil).toISOString() : undefined,
    });
  };

  const formatDiscount = (price: ProductClientPriceEntry) => {
    const base = Number(baseUnitPrice);
    if (!base || base === 0) return '';
    const discount = ((1 - Number(price.customPrice) / base) * 100);
    if (discount > 0) return `−${discount.toFixed(1)}%`;
    if (discount < 0) return `+${Math.abs(discount).toFixed(1)}%`;
    return '0%';
  };

  const isExpired = (price: ProductClientPriceEntry) => {
    if (!price.validUntil) return false;
    return new Date(price.validUntil) < new Date();
  };

  // Filtrer localement les clients (comme EditableClientSection)
  const existingClientIds = new Set(clientPrices.map((p) => p.clientId));
  const clients = allClients.filter((c: any) => {
    // Exclure les clients qui ont déjà un tarif
    if (existingClientIds.has(c.id)) return false;
    // Filtrer par recherche si le terme est renseigné
    if (!clientSearch.trim()) return true;
    const term = clientSearch.toLowerCase();
    const name = (c.fullName || c.companyName || '').toLowerCase();
    const company = (c.companyName || '').toLowerCase();
    const email = (c.email || '').toLowerCase();
    return name.includes(term) || company.includes(term) || email.includes(term);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-teal-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            {t('products.clientPricing.title', { defaultValue: 'Tarifs par client' })}
          </h3>
          {clientPrices.length > 0 && (
            <span className="px-2 py-0.5 bg-teal-50 text-teal-600 rounded-full text-xs font-medium">
              {clientPrices.length}
            </span>
          )}
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            {t('products.clientPricing.add', { defaultValue: 'Ajouter un tarif client' })}
          </button>
        )}
      </div>

      {/* Formulaire d'ajout */}
      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
          {/* Recherche client */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('products.clientPricing.selectClient', { defaultValue: 'Client' })}
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  setNewClientId('');
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder={t('products.clientPricing.searchClient', { defaultValue: 'Rechercher un client...' })}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            {isLoadingClients && showDropdown && (
              <p className="mt-1 text-xs text-gray-500">{t('products.clientPricing.loading', { defaultValue: 'Chargement...' })}</p>
            )}
            {showDropdown && !newClientId && !isLoadingClients && (
              <div className="absolute z-20 w-full mt-1 border border-gray-200 rounded-lg bg-white shadow-lg max-h-48 overflow-y-auto">
                {clients.length > 0 ? (
                  clients.map((c: any) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setNewClientId(c.id);
                        setClientSearch(c.fullName || c.companyName || c.email || '');
                        setShowDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-teal-50 transition-colors border-b border-gray-50 last:border-0"
                    >
                      <span className="font-medium text-gray-900">{c.fullName || c.companyName}</span>
                      {c.email && <span className="text-gray-500 ml-2 text-xs">{c.email}</span>}
                    </button>
                  ))
                ) : (
                  <p className="px-3 py-2 text-xs text-gray-400">{t('products.clientPricing.noClientFound', { defaultValue: 'Aucun client trouvé' })}</p>
                )}
              </div>
            )}
          </div>

          {/* Prix + Notes */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('products.clientPricing.price', { defaultValue: 'Prix négocié (€ HT)' })}
              </label>
              <input
                type="number"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value ? Number(e.target.value) : '')}
                min={0}
                step={0.01}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('products.clientPricing.notes', { defaultValue: 'Notes (optionnel)' })}
              </label>
              <input
                type="text"
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                maxLength={500}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>

          {/* Dates de validité */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('products.clientPricing.validFrom', { defaultValue: 'Valide à partir du' })}
              </label>
              <input
                type="date"
                value={newValidFrom}
                onChange={(e) => setNewValidFrom(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('products.clientPricing.validUntil', { defaultValue: "Valide jusqu'au" })}
              </label>
              <input
                type="date"
                value={newValidUntil}
                onChange={(e) => setNewValidUntil(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={resetForm}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.cancel', { defaultValue: 'Annuler', ns: 'common' })}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!newClientId || newPrice === '' || setMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {setMutation.isPending
                ? t('products.clientPricing.saving', { defaultValue: 'Enregistrement...' })
                : t('products.clientPricing.save', { defaultValue: 'Enregistrer' })}
            </button>
          </div>

          {setMutation.isError && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {(setMutation.error as Error)?.message || t('products.clientPricing.error', { defaultValue: 'Erreur lors de la sauvegarde' })}
            </div>
          )}
        </div>
      )}

      {/* Liste des tarifs existants */}
      {isLoading ? (
        <div className="text-xs text-gray-500 py-2">
          {t('products.clientPricing.loading', { defaultValue: 'Chargement...' })}
        </div>
      ) : clientPrices.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-xs">
                <th className="px-3 py-2 text-left font-medium">
                  {t('products.clientPricing.client', { defaultValue: 'Client' })}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('products.clientPricing.catalogPrice', { defaultValue: 'Catalogue' })}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('products.clientPricing.negotiatedPrice', { defaultValue: 'Négocié' })}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('products.clientPricing.discount', { defaultValue: 'Écart' })}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('products.clientPricing.notesHeader', { defaultValue: 'Notes' })}
                </th>
                <th className="px-3 py-2 text-center font-medium w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {clientPrices.map((price) => (
                <tr
                  key={price.id}
                  className={`hover:bg-gray-50 transition-colors ${isExpired(price) ? 'opacity-50' : ''}`}
                >
                  <td className="px-3 py-2 text-gray-900 font-medium">
                    {price.clientName}
                    {isExpired(price) && (
                      <span className="ml-1 text-[10px] text-red-500 font-normal">
                        {t('products.clientPricing.expired', { defaultValue: '(expiré)' })}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-500">
                    {Number(baseUnitPrice).toFixed(2)} €
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-teal-600">
                    {Number(price.customPrice).toFixed(2)} €
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={`text-xs font-medium ${
                      Number(price.customPrice) < Number(baseUnitPrice)
                        ? 'text-green-600'
                        : Number(price.customPrice) > Number(baseUnitPrice)
                          ? 'text-red-500'
                          : 'text-gray-400'
                    }`}>
                      {formatDiscount(price)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs truncate max-w-[150px]">
                    {price.notes || '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => removeMutation.mutate(price.clientId)}
                      disabled={removeMutation.isPending}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title={t('products.clientPricing.remove', { defaultValue: 'Supprimer' })}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-xs text-gray-400 py-2 text-center">
          {t('products.clientPricing.empty', { defaultValue: 'Aucun tarif client défini' })}
        </div>
      )}

      {removeError && (
        <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          {removeError}
        </div>
      )}
    </div>
  );
};
